import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { CashbackIcon, DiscountIcon, ExperienceIcon, GiftCardIcon, LeftArrowIcon, MembershipIcon, RightArrowIcon } from '../../../assets/svg';

import { useAuth } from '../../../hoc/AuthContext';
import useWindowSize from '../../../hooks/useWindowSize';

import { COMMERCE_MODULE, SUBSCRIPTION_LISTING, TRAVEL_MODULE } from '../../../Routes/RoutesContant';

import { defaultTextColor, HighlightedText, subscritptionDefaultColor } from '../../../Utils/CommonFunctions';
import { cardTitleToPath } from '../../../Utils/Constant';

import Button from '../../CommonComponents/Button';
import AccessMembershipExperienceCardDesign from '../../CommonComponents/CardsDesign/AccessMembershipExperienceCardDesign';
import CommerceCardDesign from '../../CommonComponents/CardsDesign/CommerceCardDesign';
import LatestCashbackCardDesign from '../../CommonComponents/CardsDesign/LatestCashbackCardDesign';
import SubscriptionCardDesign from '../../CommonComponents/CardsDesign/SubscriptionCardDesign';
import TopDiscountCardDesign from '../../CommonComponents/CardsDesign/TopDiscountCardDesign';
import TopGiftCardDesign from '../../CommonComponents/CardsDesign/TopGiftCardDesign';
import { SkeletonCardSectionDesign } from '../../CommonComponents/SkeletonUI/LandingPageSkeleton';

export default function CardDesign({ data, handleLinkClick, title, subTitle, cardType, showArrow, isLoading }) {
  const isMobile = useWindowSize();
  const navigate = useNavigate();
  const swiperRef = useRef(null); // Ref to access the Swiper instance
  const { integratorTheme, isAuthenticated, setCardInfo, setShowSignUp } = useAuth();

  const [currentSlide, setCurrentSlide] = useState(0); // Track current slide index

  const cardTypeConfig = {
    giftcard: {
      icon: <GiftCardIcon width={"40px"} height={"40px"} />,
      heading: title,
      cardComponent: TopGiftCardDesign,
    },
    cashbackOffer: {
      icon: <DiscountIcon width={"40px"} height={"40px"} />,
      heading: title,
      cardComponent: LatestCashbackCardDesign,
    },
    discountOffer: {
      icon: <CashbackIcon width={"40px"} height={"40px"} />,
      heading: title,
      cardComponent: TopDiscountCardDesign,
    },
    accessNmembership: {
      icon: <img src={MembershipIcon} alt="Access & Membership" loading="lazy" />,
      heading: title,
      cardComponent: AccessMembershipExperienceCardDesign,
    },
    amazingExperinces: {
      icon: <ExperienceIcon width={"40px"} height={"40px"} />,
      heading: title,
      cardComponent: AccessMembershipExperienceCardDesign,
    },
    subscription: {
      heading: title,
      subHeading: subTitle,
      cardComponent: SubscriptionCardDesign,
    },
    commerce: {
      heading: title,
      subHeading: subTitle,
      cardComponent: CommerceCardDesign,
    },
  };

  // Handle view button click
  const handleViewButton = (moduleType) => {
    if (moduleType === "amazingExperinces") {
      return;
    }

    if (!isAuthenticated) {
      // Show login modal if not authenticated
      // setShowLogin(true);
      setShowSignUp(true);
      setCardInfo({ [moduleType]: true });
    } else {
      // Redirect based on the module type
      switch (moduleType) {
        case "subscription":
          navigate(SUBSCRIPTION_LISTING);
          break;
        case "Travel":
          navigate(TRAVEL_MODULE);
          break;
        case "commerce":
          navigate(COMMERCE_MODULE);
          break;
        case "cashbackOffer":
          navigate("/rewards/cashback");
          break;
        case "discountOffer":
          navigate("/rewards/discount");
          break;
        case "giftcard":
          navigate("/rewards/giftcard");
          break;
        case "accessNmembership":
          navigate("/rewards/membership");
          break;
        case "rewards":
          navigate("/rewards");
          break;
        default:
          navigate("/");
      }
    }
  };

  const { cardComponent: CardComponent } = cardTypeConfig[cardType] || {};

  const totalSlides = data?.length;

  const handlePrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
      setCurrentSlide(swiperRef.current.swiper.activeIndex);
    }
  };

  const handleNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
      setCurrentSlide(swiperRef.current.swiper.activeIndex);
    }
  };

  const getResponsiveConfig = () => {
    switch (cardType) {
      case "subscription":
        return {
          1440: { slidesPerView: 8 },
          1300: { slidesPerView: 8 },
          1280: { slidesPerView: 7.2 },
          1080: { slidesPerView: 6.5 },
          900: { slidesPerView: 6 },
          800: { slidesPerView: 5 },
          768: { slidesPerView: 4.5 },
          767: { slidesPerView: 5 },
          640: { slidesPerView: 4 },
          600: { slidesPerView: 3.8 },
          550: { slidesPerView: 3.5 },
          500: { slidesPerView: 3.2 },
          470: { slidesPerView: 3 },
          450: { slidesPerView: 2.5 },
          370: { slidesPerView: 2.5 },
          320: { slidesPerView: 2.5 },
          310: { slidesPerView: 2.5 },
        };
      case "commerce":
        return {
          1440: { slidesPerView: 7 },
          1300: { slidesPerView: 6.2 },
          1280: { slidesPerView: 6 },
          1080: { slidesPerView: 4.5 },
          900: { slidesPerView: 4.5 },
          800: { slidesPerView: 4 },
          768: { slidesPerView: 3.2 },
          767: { slidesPerView: 5 },
          640: { slidesPerView: 3 },
          550: { slidesPerView: 2.5 },
          500: { slidesPerView: 2.3 },
          470: { slidesPerView: 2.2 },
          450: { slidesPerView: 2.5 },
          370: { slidesPerView: 2.4 },
          320: { slidesPerView: 2 },
          310: { slidesPerView: 1.5 },
        };
      default:
        return {
          1440: { slidesPerView: 6 },
          1300: { slidesPerView: 6 },
          1280: { slidesPerView: 5.2 },
          1080: { slidesPerView: 4.5 },
          900: { slidesPerView: 4 },
          800: { slidesPerView: 3.5 },
          768: { slidesPerView: 3.2 },
          767: { slidesPerView: 3 },
          600: { slidesPerView: 2.6 },
          550: { slidesPerView: 2.5 },
          500: { slidesPerView: 2.3 },
          470: { slidesPerView: 2.2 },
          450: { slidesPerView: 2 },
          370: { slidesPerView: 2 },
          320: { slidesPerView: 2 },
          310: { slidesPerView: 1.5 },
        };
    }
  };

  return isLoading ? (<SkeletonCardSectionDesign />) : (
    <>
      {data !== null && totalSlides > 0 ? (
        <div className="container flex md:pb-8 mt-4 md:mt-0 flex-col pl-5 -pr-5 md:px-5 mx-auto relative">
          <div className="flex gap-2.5 items-start md:items-center justify-between">
            <div className="flex gap-2.5 items-center">
              <div className="flex flex-col gap-0 md:gap-1">
                <div className={`font-Inter ${showArrow ? "text-xl md:text-3xl" : "text-2xl md:text-4xl"} font-semibold`}>
                  <div className={`font-Inter font-semibold cursor-pointer ${cardType === "subscription" || cardType === "commerce" ? "w-full" : "w-full"}`}
                    onClick={() => handleViewButton(cardType === "subscription" ? "subscription" : "rewards")}
                  >
                    <HighlightedText
                      text={title}
                      integratorTheme={integratorTheme}
                    />
                  </div>
                </div>
                <div className={`font-Inter ${cardType === "subscription" || cardType === "commerce" ? "w-full" : "w-full"}`}>
                  <HighlightedText
                    text={subTitle}
                    integratorTheme={integratorTheme}
                  />
                </div>
              </div>
            </div>
            {showArrow ? (
              <>
                <button onClick={handlePrev} className="carousel-custom-arrow left-arrow">
                  <LeftArrowIcon className={`w-[24px] h-[24px] md:w-[35px] md:h-[35px] stroke-[${currentSlide === 0 ? "#737373" : "#000"}]`} />
                </button>
                <button onClick={handleNext} className="carousel-custom-arrow right-arrow">
                  <RightArrowIcon className={`w-[24px] h-[24px] md:w-[35px] md:h-[35px] stroke-[${currentSlide === totalSlides - 1 ? "#737373" : "#000"}]`} />
                </button>
              </>
            ) : (
              <Button variant="primary" size="sm" theme={integratorTheme} className="md:px-5 mr-2 md:mr-0 text-base font-medium h-auto p-2.5 hidden md:block md:py-0 w-[150px] md:w-auto md:h-10" onClick={() => handleViewButton(cardType)}>
                View All
              </Button>
            )}
          </div>
          {(cardType === "subscription" || cardType === "commerce") ? null :
            (!showArrow && (
              <div className="flex justify-end mt-2">
                <Button
                  variant="primary"
                  size="sm"
                  theme={integratorTheme}
                  className="md:px-5 block md:hidden mr-2 md:mr-0 text-base font-medium h-auto p-2.5 md:py-0 w-fit md:w-auto md:h-10"
                  onClick={() => handleViewButton("rewards")}
                >
                  View All
                </Button>
              </div>)
            )}

          <div className="carousel-wrapper">
            <Swiper
              ref={swiperRef}
              modules={[Navigation, Autoplay]}
              spaceBetween={16}
              slidesPerView={6}
              autoplay={{ delay: showArrow ? 5000 : 2000, disableOnInteraction: false }}
              onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
              breakpoints={getResponsiveConfig()}
              className='swiperCarousalCards'
              loop={true}
            >
              {data?.map((item) => {
                const path = cardTitleToPath(item?.offer_type);
                return (
                  <SwiperSlide key={item?.id}>
                    <div className="flex flex-col slider justify-center items-start">
                      {cardType === "amazingExperinces" ? (
                        <div>
                          <CardComponent
                            mobileViewCardDesign={true}
                            isMobile={isMobile}
                            image={item?.brand?.logo}
                            item={item}
                          />
                          <div
                            title={item?.brand?.name}
                            className="font-Inter-Tight w-[140px] md:w-[190px] truncate text-[16px] font-medium pt-2"
                          >
                            {item?.brand?.name}
                          </div>
                          {item?.saving_amount && (
                            <div style={{ color: defaultTextColor(integratorTheme) }} className="font-Inter-Tight text-base w-[140px] md:w-[190px] truncate font-semibold">
                              {item?.saving_amount}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={
                            isAuthenticated
                              ? cardType === "subscription"
                                ? SUBSCRIPTION_LISTING
                                : cardType === "commerce"
                                  ? `/commerce/${item?.slug}`
                                  : `/rewards/${path}/${item?.id}/${item?.slug}`
                              : "/"
                          }
                          onClick={() =>
                            handleLinkClick(
                              item,
                              { popularCategory: false },
                              cardType === "subscription" && true,
                              cardType === "commerce" && true
                            )
                          }
                        >
                          <CardComponent
                            mobileViewCardDesign={true}
                            isMobile={isMobile}
                            image={item?.brand?.logo}
                            item={item}
                          />
                          <div
                            title={item?.brand?.name}
                            className="font-Inter-Tight w-[140px] md:w-[190px] truncate text-[16px] font-medium pt-2"
                          >
                            {item?.brand?.name}
                          </div>
                          {cardType === "subscription" ? (
                            item?.saving_amount && (
                              <div
                                style={{ color: subscritptionDefaultColor(item?.saving_amount) }}
                                className="font-Inter-Tight text-base w-[140px] md:w-[190px] truncate font-semibold"
                              >
                                {item?.saving_amount}
                              </div>
                            )
                          ) : (
                            item?.saving_amount && (
                              <div
                                style={{ color: defaultTextColor(integratorTheme) }}
                                className="font-Inter-Tight text-base w-[140px] md:w-[190px] truncate font-semibold"
                              >
                                {item?.saving_amount}
                              </div>
                            )
                          )}
                        </Link>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      ) : null}
    </>
  );
}